import { useEffect } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { z } from "zod";
import { adWriteSchema, type AdDto, type AdsType, type AdWriteInput } from "@ads/shared";

/** Human labels for the product type, shown when it's locked from the upload menu. */
const PRODUCT_TYPE_LABELS: Record<AdsType, string> = {
  newsletter: "Newsletter",
  event: "Event",
  survey: "Survey",
  all: "All Product Types",
};

/** The form's input type (pre-resolve): showOnArticlePages is optional thanks to its default. */
type AdFormInput = z.input<typeof adWriteSchema>;
import { createAd, fetchAd, updateAd } from "../api/ads.js";
import { ApiError } from "../api/client.js";
import { ImageUpload } from "../components/ImageUpload.js";

/** AdDto → form values (the AdWriteInput subset) for edit pre-fill. */
function toFormValues(ad: AdDto): AdWriteInput {
  return {
    title: ad.title,
    position: ad.position,
    startDate: ad.startDate,
    endDate: ad.endDate,
    status: ad.status,
    linkType: ad.linkType,
    linkValue: ad.linkValue,
    adsType: ad.adsType,
    showOnArticlePages: ad.showOnArticlePages,
    image: ad.image,
    orgIds: ad.orgIds,
  };
}

/**
 * Create/Edit default-ad form (US-D1–D6, US-E1/E2, US-G4). Validates with the shared
 * `adWriteSchema` (zodResolver) so the form can't drift from the backend. On create the
 * target orgs come from router state (the picker's selection, US-D5) or the `:orgId`; on
 * edit they are pre-filled from the ad's placement rows.
 */
export function AdFormPage({ mode }: { mode: "create" | "edit" }) {
  const { orgId, id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const routeState = useLocation().state as { orgIds?: string[]; adsType?: AdsType } | null;
  const createOrgIds = routeState?.orgIds ?? (orgId ? [orgId] : []);
  // When the product type was chosen up-front via the upload menu, lock it in the form
  // (legacy passes it as a hidden field). Absent (edit, or AdListPage button) → editable.
  const lockedAdsType = mode === "create" ? routeState?.adsType : undefined;

  const editQuery = useQuery({
    queryKey: ["ad", id],
    queryFn: () => fetchAd(id!),
    enabled: mode === "edit" && !!id,
  });

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<AdFormInput, unknown, AdWriteInput>({
    resolver: zodResolver(adWriteSchema),
    defaultValues: {
      title: "",
      position: "first",
      startDate: "",
      endDate: "",
      status: 1,
      linkType: "weburl",
      linkValue: "",
      adsType: lockedAdsType ?? "newsletter",
      showOnArticlePages: false,
      image: "",
      orgIds: createOrgIds,
    },
    values: editQuery.data ? toFormValues(editQuery.data) : undefined,
  });

  // US-D6 gate: the "show on article pages" box only applies to newsletter/all; coerce off otherwise.
  const adsType = watch("adsType");
  const articleAllowed = adsType === "newsletter" || adsType === "all";
  useEffect(() => {
    if (!articleAllowed) setValue("showOnArticlePages", false);
  }, [articleAllowed, setValue]);

  const image = watch("image");
  const orgIds = watch("orgIds");
  const backTo = orgIds && orgIds.length ? `/orgs/${orgIds[0]}/ads` : "/";

  const save = useMutation({
    mutationFn: (values: AdWriteInput) =>
      mode === "create" ? createAd(values) : updateAd(id!, values),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ads"] });
      navigate(backTo);
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError) {
        const fieldErrors = (err.body as { message?: { fieldErrors?: Record<string, string[]> } })?.message
          ?.fieldErrors;
        if (fieldErrors) {
          for (const [field, msgs] of Object.entries(fieldErrors)) {
            setError(field as keyof AdWriteInput, { message: msgs.join(", ") });
          }
        }
        setError("root.server", { message: err.message });
      } else {
        setError("root.server", { message: String(err) });
      }
    },
  });

  if (mode === "edit" && editQuery.isLoading) {
    return <main className="state-screen"><p>Loading…</p></main>;
  }
  if (mode === "edit" && editQuery.isError) {
    return (
      <main className="state-screen" role="alert">
        <div className="state-card"><h1>Ad not found</h1><Link to="/" className="back-link">← Organisations</Link></div>
      </main>
    );
  }

  return (
    <main className="ad-form">
      <Link to={backTo} className="back-link">← Back</Link>
      <h1>{mode === "create" ? "New default ad" : "Edit default ad"}</h1>
      {mode === "create" && (
        <p className="muted-note">
          Targeting {createOrgIds.length} organisation{createOrgIds.length === 1 ? "" : "s"}.
        </p>
      )}

      <form onSubmit={handleSubmit((v) => save.mutate(v))} noValidate>
        <label className="field">
          <span>Advertisement title</span>
          <input type="text" {...register("title")} aria-invalid={!!errors.title} />
          {errors.title && <span className="field-error">{errors.title.message}</span>}
        </label>

        <fieldset className="field">
          <legend>Position</legend>
          <label><input type="radio" value="first" {...register("position")} /> First (above paid ads)</label>
          <label><input type="radio" value="last" {...register("position")} /> Last (below paid ads)</label>
        </fieldset>

        <div className="field-row">
          <label className="field">
            <span>Start date</span>
            <input type="date" {...register("startDate")} aria-invalid={!!errors.startDate} />
            {errors.startDate && <span className="field-error">{errors.startDate.message}</span>}
          </label>
          <label className="field">
            <span>End date</span>
            <input type="date" {...register("endDate")} aria-invalid={!!errors.endDate} />
            {errors.endDate && <span className="field-error">{errors.endDate.message}</span>}
          </label>
        </div>

        <label className="field">
          <span>Status</span>
          <select {...register("status", { valueAsNumber: true })}>
            <option value={1}>Active</option>
            <option value={0}>Inactive</option>
          </select>
        </label>

        <label className="field">
          <span>Product type</span>
          {lockedAdsType ? (
            <>
              <input type="hidden" {...register("adsType")} />
              <output className="field-readonly">{PRODUCT_TYPE_LABELS[lockedAdsType]}</output>
            </>
          ) : (
            <select {...register("adsType")}>
              <option value="newsletter">Newsletter</option>
              <option value="event">Event</option>
              <option value="survey">Survey</option>
              <option value="all">All</option>
            </select>
          )}
        </label>

        <div className="field-row">
          <label className="field">
            <span>Link type</span>
            <select {...register("linkType")}>
              <option value="weburl">Web link</option>
              <option value="email">Email</option>
            </select>
          </label>
          <label className="field">
            <span>Link value</span>
            <input type="text" {...register("linkValue")} aria-invalid={!!errors.linkValue} />
            {errors.linkValue && <span className="field-error">{errors.linkValue.message}</span>}
          </label>
        </div>

        <label className="field checkbox">
          <input type="checkbox" {...register("showOnArticlePages")} disabled={!articleAllowed} />
          <span>Display this ad on public article pages</span>
          {!articleAllowed && <span className="muted-note"> (newsletter / all only)</span>}
        </label>

        <div className="field">
          <span>Image</span>
          <input type="hidden" {...register("image")} />
          <ImageUpload
            value={image}
            existingName={mode === "edit" ? editQuery.data?.image : undefined}
            onChange={(name) => setValue("image", name ?? "", { shouldValidate: true })}
          />
          {errors.image && <span className="field-error">{errors.image.message}</span>}
        </div>

        {errors.root?.server && <p className="field-error" role="alert">{errors.root.server.message}</p>}

        <div className="form-actions">
          <button type="submit" className="btn primary" disabled={isSubmitting || save.isPending}>
            {mode === "create" ? "Create default ad" : "Save changes"}
          </button>
          <Link to={backTo} className="btn">Cancel</Link>
        </div>
      </form>
    </main>
  );
}
